import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import RatingReview from 'components/RatingReview';
import LinearProgress from 'components/LinearProgress';
import { postReview } from 'actions/reviews';
import { isAuthenticated } from 'helpers/authToken';
import useDataListFactory from 'services/dataList';
import reviewsEndPoint from 'endPoints/reviews';
import NeedAuthDialog from 'components/NeedAuthDialog';
import { COLUMNS_LIMIT, MAX_MESSAGE_LIMIT } from 'variables/main';
const useDataList = (props, options) => useDataListFactory(props, options);
const ROWS_LIMIT = 6;

const Background = styled('section')(({ theme }) => ({
  backgroundColor: theme.palette.textPrimary.main
}));

const Wrapper = styled('div')(({ theme }) => ({
  padding: '60px 100px',
  ...theme.wrapper,
  maxWidth: 1240,
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20,
    paddingBottom: 60,
    paddingTop: 20 // Reduced from 40px to 20px
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 35,
  color: theme.palette.white.main,
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: 28,
    lineHeight: '27px'
  }
}));

const CenterButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  margin: '0 auto',
  marginTop: 40,
  [theme.breakpoints.down('sm')]: {
    marginTop: 25,
    width: '100%'
  }
}));

const CardWrapper = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 20,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr'
  }
}));

const UserName = styled(Typography)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const UserMessage = styled(Typography)(({ theme, full }) => ({
  ...(full !== 'true' ? theme.textCut('6') : {})
}));

const StarsWrapper = styled('span')(() => ({
  display: 'flex',
  gap: 5,
  '& svg': {
    width: 20,
    height: 20
  }
}));

const Card = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  padding: 20,
  borderRadius: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: 10
}));

const FormWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 30,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: 20
  }
}));

const LeftSidebar = styled('div')(() => ({
  flex: 1
}));

const RightSidebar = styled('div')(() => ({
  flex: 2.5
}));

const DialogActionsWrapper = styled(DialogActions)(({ theme, center }) => ({
  padding: 30,
  paddingTop: 20,
  justifyContent: center ? 'center' : 'flex-end',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'flex-start'
  }
}));

const DialogContentWrapper = styled(DialogContent)(({ center }) => ({
  padding: 30,
  paddingBottom: 0,
  textAlign: center ? 'center' : 'left'
}));

const DialogTitleWrapper = styled(DialogTitle)(({ center }) => ({
  padding: 30,
  paddingBottom: 0,
  marginBottom: 20,
  textAlign: center ? 'center' : 'left'
}));

const FieldHeadline = styled(Typography)(() => ({
  marginBottom: 10,
  fontSize: 16
}));

const RatingWrapper = styled('div')(() => ({
  marginBottom: 20
}));

const StyledMultiline = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    padding: 0
  }
}));

const ButtonLg = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const ButtonSM = styled(Button)(({ theme }) => ({
  display: 'none',
  backgroundColor: theme.palette.white.main,
  borderRadius: 8,
  border: `1px solid ${theme.palette.primary.main}`,
  color: theme.palette.textPrimary.main,
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    width: '100%',
    margin: '0 auto',
    marginTop: 15
  }
}));

const NoReviews = styled(Typography)(({ theme }) => ({
  color: theme.palette.white.main
}));

const UserAvatar = styled('img')(() => ({
  width: 30,
  height: 30,
  borderRadius: '50%',
  marginRight: 10
}));

const UserFlexName = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center'
}));

const LeaveReview = ({ open, handleClose, t, handleSave, needAuth }) => {
  const [rating, setRating] = React.useState(0);
  const [message, setMessage] = React.useState('');
  const [success, setSuccess] = React.useState(false);
  const [errors, setErrors] = React.useState([]);

  const handleSubmit = React.useCallback(async () => {
    setErrors([]);

    if (!rating) {
      setErrors((prev) => [...prev, 'rating']);
    }

    if (!message) {
      setErrors((prev) => [...prev, 'message']);
    }

    if (!rating || !message) {
      return;
    }

    const isSuccess = await handleSave({
      mark: rating,
      text: message
    });

    if (isSuccess instanceof Error) {
      return;
    }

    setSuccess(true);
  }, [message, rating, handleSave]);

  const handleFinish = React.useCallback(() => {
    setSuccess(false);
    handleClose();
  }, [handleClose]);

  if (success) {
    return (
      <Dialog open={success} onClose={handleFinish}>
        <DialogTitleWrapper center="true">{t('ReviewSuccessTitle')}</DialogTitleWrapper>
        <DialogContentWrapper center="true">
          <Typography>{t('ReviewSuccessText')}</Typography>
        </DialogContentWrapper>
        <DialogActionsWrapper center="true">
          <Button variant="outlined" color="primary" onClick={handleFinish} aria-label={t('Close')}>
            {t('Close')}
          </Button>
        </DialogActionsWrapper>
      </Dialog>
    );
  }

  if (needAuth) {
    return <NeedAuthDialog handleClose={handleClose} needAuth={needAuth} />;
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth={true} maxWidth={'lg'} scroll={'body'}>
      <DialogContentWrapper>
        <FormWrapper>
          <LeftSidebar>
            <FieldHeadline>{t('ReviewStars')}</FieldHeadline>
            <RatingWrapper>
              <RatingReview rating={rating} setRating={setRating} />
              <div>
                {errors.includes('rating') ? (
                  <FormControl variant="standard" error={true}>
                    <FormHelperText>{t('RequiredError')}</FormHelperText>
                  </FormControl>
                ) : null}
              </div>
            </RatingWrapper>
          </LeftSidebar>
          <RightSidebar>
            <FieldHeadline>{t('ReviewMessage')}</FieldHeadline>
            <StyledMultiline
              name="message"
              placeholder={t('ReviewMessagePlaceholder')}
              rows={4}
              fullWidth={true}
              multiline={true}
              value={message}
              error={errors.includes('message')}
              onChange={(e) => setMessage(e.target.value)}
              slotProps={{
                htmlInput: {
                  maxLength: MAX_MESSAGE_LIMIT,
                  'aria-label': t('ReviewMessagePlaceholder')
                }
              }}
            />
            {errors.includes('message') ? (
              <FormControl variant="standard" error={true}>
                <FormHelperText>{t('RequiredError')}</FormHelperText>
              </FormControl>
            ) : null}
          </RightSidebar>
        </FormWrapper>
      </DialogContentWrapper>
      <DialogActionsWrapper>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          aria-label={t('SendReview')}>
          {t('SendReview')}
        </Button>
      </DialogActionsWrapper>
    </Dialog>
  );
};

const RenderText = ({ text }) => {
  const t = useTranslate('Reviews');
  const pRef = React.useRef(null);
  const [rows, setRows] = React.useState(0);
  const [showMore, setShowMore] = React.useState(null);

  React.useEffect(() => {
    if (pRef.current) {
      const pElement = pRef.current;
      const lineHeight = parseFloat(window.getComputedStyle(pElement).lineHeight);
      const height = pElement.offsetHeight;
      const rowCount = Math.ceil(height / lineHeight);
      setRows(rowCount);
    }
  }, []);

  return (
    <>
      <UserMessage variant="body1" title={text} ref={pRef} full={showMore ? 'true' : null}>
        {text}
      </UserMessage>
      {rows >= ROWS_LIMIT ? (
        <Button
          onClick={() => {
            setShowMore(!showMore);
            import('services/analytics').then(({ default: A }) => A.event('ReviewShowMoreClick'));
          }}>
          {t(showMore ? 'ShowLess' : 'ShowMore')}
        </Button>
      ) : null}
    </>
  );
};

const Reviews = ({ currentId: id, endPoint }) => {
  const t = useTranslate('Reviews');
  const dispatch = useDispatch();
  const [needAuth, setNeedAuth] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const {
    data: reviews,
    loading,
    actions,
    page,
    count,
    perPage
  } = useDataList(reviewsEndPoint(endPoint, id), {
    perPage: COLUMNS_LIMIT
  });

  const handleChangePage = React.useCallback(async () => {
    actions.onChangePage(page, true, true);
  }, [actions, page]);

  const handleClickOpen = () => {
    if (!isAuthenticated()) {
      setNeedAuth(true);
    }
    import('services/analytics').then(({ default: A }) => A.event('LeaveReviewClick'));
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setNeedAuth(false);
  };

  const handlePostReview = React.useCallback(
    async (review) => {
      const result = await dispatch(postReview(endPoint, id, review));
      return result;
    },
    [dispatch, id, endPoint]
  );

  const limitReached = React.useMemo(() => count <= page * perPage, [count, page, perPage]);

  return (
    <Background data-reviews-section>
      <Wrapper>
        <Title variant={'h2'}>
          <span>{t('Reviews')}</span>
          <ButtonLg
            variant="contained"
            color="primary"
            onClick={handleClickOpen}
            aria-label={t('LeaveReview')}>
            {t('LeaveReview')}
          </ButtonLg>
        </Title>

        <CardWrapper>
          {(reviews || []).map(({ userAvatar, userName, text, mark }, index) => {
            return (
              <Card key={index}>
                <UserName variant="body1">
                  <UserFlexName>
                    <UserAvatar src={userAvatar} alt={userName} />
                    {userName}
                  </UserFlexName>
                  <StarsWrapper>
                    <RatingReview rating={mark} />
                  </StarsWrapper>
                </UserName>
                <RenderText text={text} />
              </Card>
            );
          })}
        </CardWrapper>

        {loading ? (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
          </Box>
        ) : null}

        {!(reviews || []).length && !loading ? (
          <NoReviews variant="body1">{t('NoReviews')}</NoReviews>
        ) : null}

        {limitReached ? null : (
          <CenterButton
            variant="contained"
            color="primary"
            onClick={handleChangePage}
            aria-label={t('MoreReviews')}>
            {t('MoreReviews')}
          </CenterButton>
        )}

        <ButtonSM variant="outlined" color="primary" onClick={handleClickOpen}>
          {t('LeaveReview')}
        </ButtonSM>
        <LeaveReview
          t={t}
          needAuth={needAuth}
          open={open}
          handleClose={handleClose}
          handleSave={handlePostReview}
        />
      </Wrapper>
    </Background>
  );
};

export default Reviews;
